import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Check, X } from 'lucide-react';
import { config } from '../../../config';

interface Plan {
    id: number;
    name: string;
    description: string;
    price: number;
    maxUsers: number;
    maxSectors: number;
    maxConnections: number;
    isActive: boolean;
}

const AdminPlans: React.FC = () => {
    const [plans, setPlans] = useState<Plan[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState<Plan | null>(null);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: 0,
        maxUsers: 3,
        maxSectors: 1,
        maxConnections: 1,
        isActive: true
    });

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${config.API_BASE_URL}/platform/plans`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setPlans(response.data);
        } catch (error) {
            console.error('Error fetching plans:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (plan?: Plan) => {
        if (plan) {
            setEditingPlan(plan);
            setFormData({
                name: plan.name,
                description: plan.description || '',
                price: plan.price,
                maxUsers: plan.maxUsers,
                maxSectors: plan.maxSectors,
                maxConnections: plan.maxConnections,
                isActive: plan.isActive
            });
        } else {
            setEditingPlan(null);
            setFormData({
                name: '',
                description: '',
                price: 0,
                maxUsers: 3,
                maxSectors: 1,
                maxConnections: 1,
                isActive: true
            });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            if (editingPlan) {
                await axios.put(`${config.API_BASE_URL}/platform/plans/${editingPlan.id}`,
                    { ...formData, id: editingPlan.id },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } else {
                await axios.post(`${config.API_BASE_URL}/platform/plans`,
                    formData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }
            setIsModalOpen(false);
            fetchPlans();
        } catch (error) {
            console.error('Error saving plan:', error);
            alert('Erro ao salvar plano');
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Tem certeza que deseja excluir este plano?')) return;

        try {
            await axios.delete(`${config.API_BASE_URL}/platform/plans/${id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            fetchPlans();
        } catch (error) {
            console.error('Error deleting plan:', error);
            alert('Erro ao excluir plano');
        }
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Gerenciar Planos</h1>
                <button
                    onClick={() => handleOpenModal()}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors"
                >
                    <Plus size={20} className="mr-2" />
                    Novo Plano
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {plans.map((plan) => (
                    <div key={plan.id} className={`bg-white rounded-xl shadow-sm border-2 ${plan.isActive ? 'border-transparent' : 'border-gray-200 opacity-75'} overflow-hidden transition-all hover:shadow-md`}>
                        <div className={`p-6 ${!plan.isActive ? 'bg-gray-50' : ''}`}>
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                                    <p className="text-gray-500 text-sm mt-1">{plan.description}</p>
                                </div>
                                <div className="text-right">
                                    <span className="text-2xl font-bold text-blue-600">R$ {plan.price}</span>
                                    <span className="text-gray-400 text-xs block">/mês</span>
                                </div>
                            </div>

                            <div className="space-y-3 mb-6">
                                <div className="flex items-center text-gray-600 text-sm">
                                    <span className="bg-blue-100 text-blue-600 p-1 rounded mr-3 text-xs font-bold">USUÁRIOS</span>
                                    {plan.maxUsers} max
                                </div>
                                <div className="flex items-center text-gray-600 text-sm">
                                    <span className="bg-[#0C85BA]/15 text-[#0C85BA] p-1 rounded mr-3 text-xs font-bold">SETORES</span>
                                    {plan.maxSectors} max
                                </div>
                                <div className="flex items-center text-gray-600 text-sm">
                                    <span className="bg-purple-100 text-purple-600 p-1 rounded mr-3 text-xs font-bold">WHATSAPP</span>
                                    {plan.maxConnections} max
                                </div>
                                <div className="flex items-center text-sm pt-2">
                                    <span className={`px-2 py-1 rounded text-xs font-bold ${plan.isActive ? 'bg-[#0C85BA]/15 text-[#0C85BA]' : 'bg-red-100 text-red-700'}`}>
                                        {plan.isActive ? 'ATIVO' : 'INATIVO'}
                                    </span>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-2 pt-4 border-t border-gray-100">
                                <button
                                    onClick={() => handleOpenModal(plan)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="Editar"
                                >
                                    <Edit2 size={18} />
                                </button>
                                <button
                                    onClick={() => handleDelete(plan.id)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Excluir"
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl w-full max-w-lg p-6">
                        <h2 className="text-2xl font-bold mb-6">{editingPlan ? 'Editar Plano' : 'Novo Plano'}</h2>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Plano</label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full p-2 border border-gray-300 rounded-lg"
                                        value={formData.name}
                                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                                    <input
                                        type="text"
                                        className="w-full p-2 border border-gray-300 rounded-lg"
                                        value={formData.description}
                                        onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Preço (R$)</label>
                                    <input
                                        type="number"
                                        required
                                        step="0.01"
                                        className="w-full p-2 border border-gray-300 rounded-lg"
                                        value={formData.price}
                                        onChange={e => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Usuários</label>
                                    <input
                                        type="number"
                                        required
                                        className="w-full p-2 border border-gray-300 rounded-lg"
                                        value={formData.maxUsers}
                                        onChange={e => setFormData({ ...formData, maxUsers: parseInt(e.target.value) })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Setores</label>
                                    <input
                                        type="number"
                                        required
                                        className="w-full p-2 border border-gray-300 rounded-lg"
                                        value={formData.maxSectors}
                                        onChange={e => setFormData({ ...formData, maxSectors: parseInt(e.target.value) })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Conexões</label>
                                    <input
                                        type="number"
                                        required
                                        className="w-full p-2 border border-gray-300 rounded-lg"
                                        value={formData.maxConnections}
                                        onChange={e => setFormData({ ...formData, maxConnections: parseInt(e.target.value) })}
                                    />
                                </div>

                                <div className="col-span-2 flex items-center mt-2">
                                    <input
                                        type="checkbox"
                                        id="isActive"
                                        className="mr-2 h-4 w-4 text-blue-600 rounded"
                                        checked={formData.isActive}
                                        onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                                    />
                                    <label htmlFor="isActive" className="text-sm font-medium text-gray-700">Plano Ativo</label>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 mt-8 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    Salvar Plano
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPlans;
